import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { configs } from '../configs';
import { authenticatedApiCall } from '../utils/authenticatedFetch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import {
  Star,
  MessageSquare,
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
  Filter,
  Package,
  Utensils,
  Coffee,
  Users,
  ShoppingBag,
} from 'lucide-react';

interface CustomerFeedbackData {
  id: string;
  rating: number;
  comment: string;
  category: string;
  platform: string;
  orderId?: string;
  contactInfo?: string;
  imageUrls?: string[];
  submittedAt: string;
  createdAt?: string;
  updatedAt?: string;
}

interface FeedbackStats {
  averageRating: number;
  totalRatings: number;
  fiveStar: number;
  fourStar: number;
  threeStar: number;
  twoStar: number;
  oneStar: number;
  recentRatings: number;
}

const ModernCustomerReviews: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();

  const [feedbacks, setFeedbacks] = useState<CustomerFeedbackData[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [imageUrlCache, setImageUrlCache] = useState<Record<string, string>>({});

  useEffect(() => {
    loadFeedbacks();
  }, [filter, sortBy]);

  useEffect(() => {
    if (feedbacks.length > 0) {
      calculateStats();
    }
  }, [feedbacks]);

  const loadFeedbacks = async () => {
    setLoading(true);
    try {
      // Fetch customer feedbacks from API with authentication
      // CRUD routes return array directly, not wrapped in { data: ... }
      const response = await authenticatedApiCall<CustomerFeedbackData[]>(
        `${configs.apiRoot}/customer_feedbacks?size=1000&page=1`
      );

      // API returns array directly from sendFormattedResponse
      const apiFeedbacks: CustomerFeedbackData[] = Array.isArray(response)
        ? response
        : [];

      console.log(
        `[CustomerReviews] Loaded ${apiFeedbacks.length} feedback entries`
      );

      if (apiFeedbacks.length > 0) {
        console.log('[CustomerReviews] Sample feedback:', apiFeedbacks[0]);
      }

      let filteredFeedbacks = apiFeedbacks;

      // Apply filters
      if (filter === 'high') {
        filteredFeedbacks = filteredFeedbacks.filter((f) => f.rating >= 4);
      } else if (filter === 'low') {
        filteredFeedbacks = filteredFeedbacks.filter((f) => f.rating <= 2);
      } else if (filter === 'platform') {
        // Keep all for platform filter (can be extended)
        filteredFeedbacks = filteredFeedbacks;
      }

      // Apply sorting
      if (sortBy === 'date') {
        filteredFeedbacks.sort((a, b) => {
          const dateA = a.submittedAt || a.createdAt || '';
          const dateB = b.submittedAt || b.createdAt || '';
          const timeA = dateA ? new Date(dateA).getTime() : 0;
          const timeB = dateB ? new Date(dateB).getTime() : 0;
          return timeB - timeA; // Newest first
        });
      } else if (sortBy === 'rating') {
        filteredFeedbacks.sort((a, b) => b.rating - a.rating);
      } else if (sortBy === 'category') {
        filteredFeedbacks.sort((a, b) => a.category.localeCompare(b.category));
      }

      setFeedbacks(filteredFeedbacks);
    } catch (error: any) {
      console.error('Error loading customer feedbacks:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        response: error?.response,
      });
      notify(
        translate(
          'pos.customer_reviews.failed_load',
          'Failed to load customer reviews'
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const totalRatings = feedbacks.length;
    const fiveStar = feedbacks.filter((f) => f.rating === 5).length;
    const fourStar = feedbacks.filter((f) => f.rating === 4).length;
    const threeStar = feedbacks.filter((f) => f.rating === 3).length;
    const twoStar = feedbacks.filter((f) => f.rating === 2).length;
    const oneStar = feedbacks.filter((f) => f.rating === 1).length;

    const averageRating =
      totalRatings > 0
        ? feedbacks.reduce((sum, f) => sum + f.rating, 0) / totalRatings
        : 0;

    // Calculate recent ratings (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentRatings = feedbacks.filter((f) => {
      const dateStr = f.submittedAt || f.createdAt || '';
      if (!dateStr) return false;
      const date = new Date(dateStr);
      return !isNaN(date.getTime()) && date >= sevenDaysAgo;
    }).length;

    setStats({
      averageRating,
      totalRatings,
      fiveStar,
      fourStar,
      threeStar,
      twoStar,
      oneStar,
      recentRatings,
    });
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'food-beverage':
      case 'food':
        return <Utensils className="h-4 w-4" />;
      case 'service':
        return <Users className="h-4 w-4" />;
      case 'atmosphere':
        return <Coffee className="h-4 w-4" />;
      case 'menu':
        return <Package className="h-4 w-4" />;
      case 'staff':
        return <Users className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const getPlatformBadge = (platform: string) => {
    const platformMap: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
      'dine-in': { label: 'Dine In', variant: 'default' },
      'delivery': { label: 'Delivery', variant: 'secondary' },
      'grab': { label: 'Grab', variant: 'outline' },
      'shopee-food': { label: 'Shopee Food', variant: 'outline' },
      'xanh-sm': { label: 'Xanh SM', variant: 'outline' },
    };

    const platformInfo = platformMap[platform.toLowerCase()] || {
      label: platform,
      variant: 'outline' as const,
    };

    return (
      <Badge variant={platformInfo.variant} className="text-xs">
        {platformInfo.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getBlobUrl = async (blobPath: string): Promise<string | null> => {
    // If already cached, return cached URL
    if (imageUrlCache[blobPath]) {
      return imageUrlCache[blobPath];
    }

    // If it's already a full URL (starts with http), return as-is
    if (blobPath.startsWith('http://') || blobPath.startsWith('https://')) {
      return blobPath;
    }

    try {
      const response = await authenticatedApiCall<{ url: string }>(
        `${configs.apiRoot}/customer_feedbacks/images/url?path=${encodeURIComponent(blobPath)}`
      );

      if (response?.url) {
        // Cache the URL
        setImageUrlCache((prev) => ({ ...prev, [blobPath]: response.url }));
        return response.url;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching blob URL for ${blobPath}:`, error);
      return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>
            {translate('pos.customer_reviews.loading', 'Loading customer reviews...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      <div className="flex flex-col items-start">
        <h1 className="text-3xl font-bold text-foreground">
          {translate('pos.customer_reviews.title', 'Customer Reviews')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {translate(
            'pos.customer_reviews.subtitle',
            'Customer feedback and ratings for your business'
          )}
        </p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Star className="h-4 w-4" />
                {translate('pos.customer_reviews.average_rating', 'Average Rating')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">
                {stats.averageRating.toFixed(1)}
              </div>
              <div className="flex items-center gap-1 mt-1">
                {getRatingStars(Math.round(stats.averageRating))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                {translate('pos.customer_reviews.total_ratings', 'Total Reviews')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalRatings}</div>
              <div className="text-sm text-muted-foreground">
                {stats.recentRatings}{' '}
                {translate('pos.customer_reviews.this_week', 'this week')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                {translate('pos.customer_reviews.high_ratings', 'High Ratings')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.fiveStar + stats.fourStar}
              </div>
              <div className="text-sm text-muted-foreground">
                {translate('pos.customer_reviews.high_ratings_desc', '4+ star reviews')}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                {translate('pos.customer_reviews.positive_feedback', 'Positive')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {stats.fiveStar}
              </div>
              <div className="text-sm text-muted-foreground">
                {translate('pos.customer_reviews.five_star', '5 star reviews')}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rating Distribution */}
      {stats && stats.totalRatings > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {translate(
                'pos.customer_reviews.rating_distribution',
                'Rating Distribution'
              )}
            </CardTitle>
            <CardDescription>
              {translate(
                'pos.customer_reviews.rating_distribution_desc',
                'Breakdown of all customer ratings'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { stars: 5, count: stats.fiveStar, color: 'bg-green-500' },
                { stars: 4, count: stats.fourStar, color: 'bg-blue-500' },
                { stars: 3, count: stats.threeStar, color: 'bg-yellow-500' },
                { stars: 2, count: stats.twoStar, color: 'bg-orange-500' },
                { stars: 1, count: stats.oneStar, color: 'bg-red-500' },
              ].map(({ stars, count, color }) => (
                <div key={stars} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-16">
                    <span className="text-sm font-medium">{stars}</span>
                    <Star className="h-3 w-3 text-yellow-400" />
                  </div>
                  <div className="flex-1">
                    <Progress
                      value={(count / stats.totalRatings) * 100}
                      className="h-2"
                    />
                  </div>
                  <div className="text-sm font-medium w-8 text-right">
                    {count}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Sorting */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {translate('pos.customer_reviews.filters_sorting', 'Filters & Sorting')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div>
              <Label htmlFor="filter">
                {translate('pos.customer_reviews.filter_by', 'Filter by:')}
              </Label>
              <select
                id="filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="ml-2 px-3 py-1 border rounded"
              >
                <option value="all">
                  {translate('pos.customer_reviews.all_reviews', 'All Reviews')}
                </option>
                <option value="high">
                  {translate(
                    'pos.customer_reviews.high_ratings_filter',
                    'High Ratings (4+)'
                  )}
                </option>
                <option value="low">
                  {translate(
                    'pos.customer_reviews.low_ratings_filter',
                    'Low Ratings (2-)'
                  )}
                </option>
              </select>
            </div>

            <div>
              <Label htmlFor="sort">
                {translate('pos.customer_reviews.sort_by', 'Sort by:')}
              </Label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="ml-2 px-3 py-1 border rounded"
              >
                <option value="date">
                  {translate('pos.customer_reviews.sort_date', 'Date (Newest)')}
                </option>
                <option value="rating">
                  {translate('pos.customer_reviews.sort_rating', 'Rating (Highest)')}
                </option>
                <option value="category">
                  {translate('pos.customer_reviews.sort_category', 'Category')}
                </option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reviews List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {translate('pos.customer_reviews.customer_reviews', 'Customer Reviews')}
          </CardTitle>
          <CardDescription>
            {translate(
              'pos.customer_reviews.customer_reviews_desc',
              'Recent customer feedback and ratings'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {feedbacks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {translate('pos.customer_reviews.no_reviews', 'No reviews found')}
            </div>
          ) : (
            <div className="space-y-4">
              {feedbacks.map((feedback) => (
                <div
                  key={feedback.id}
                  className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        {getRatingStars(feedback.rating)}
                      </div>
                      <div>
                        <div className="font-semibold">
                          {translate('pos.customer_reviews.customer', 'Customer')}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                           {formatDate(
                            feedback.submittedAt || feedback.createdAt || ''
                          )}
                        </div>
                        {feedback.contactInfo && (
                          <div className="text-sm text-primary flex items-center gap-2 mt-1 font-medium">
                            <span className="text-xs text-muted-foreground font-normal">
                              {translate('pos.customer_reviews.contact_info', 'Contact')}:
                            </span>
                            {feedback.contactInfo}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        {getCategoryIcon(feedback.category)}
                        {feedback.category}
                      </Badge>
                      {getPlatformBadge(feedback.platform)}
                      {feedback.orderId && (
                        <Badge variant="secondary" className="text-xs">
                          <ShoppingBag className="h-3 w-3 mr-1" />
                          {feedback.orderId.slice(-8)}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {feedback.comment && (
                    <div className="bg-muted/50 p-3 rounded-md mb-2">
                      <p className="text-sm">{feedback.comment}</p>
                    </div>
                  )}

                  {feedback.imageUrls && feedback.imageUrls.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {feedback.imageUrls.map((blobPath, index) => (
                        <FeedbackImage
                          key={index}
                          blobPath={blobPath}
                          index={index}
                          getBlobUrl={getBlobUrl}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Component to handle loading blob URLs for images
const FeedbackImage: React.FC<{
  blobPath: string;
  index: number;
  getBlobUrl: (path: string) => Promise<string | null>;
}> = ({ blobPath, index, getBlobUrl }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadImageUrl = async () => {
      try {
        setLoading(true);
        const url = await getBlobUrl(blobPath);
        if (isMounted) {
          if (url) {
            setImageUrl(url);
          } else {
            setError(true);
          }
        }
      } catch (err) {
        console.error(`Error loading image ${index}:`, err);
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadImageUrl();

    return () => {
      isMounted = false;
    };
  }, [blobPath, index, getBlobUrl]);

  if (error) {
    return null; // Don't render if there's an error
  }

  if (loading || !imageUrl) {
    return (
      <div className="h-20 w-20 rounded border bg-muted animate-pulse flex items-center justify-center">
        <div className="text-xs text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`Review image ${index + 1}`}
      className="h-20 w-20 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
      onClick={() => {
        // Open image in new tab/window for full view
        window.open(imageUrl, '_blank');
      }}
      onError={() => {
        setError(true);
      }}
    />
  );
};

export default ModernCustomerReviews;

